import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, Award, Download, Upload, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { apiService, PackCosmeticList } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { useSnapshotDraft, getDraftPayload, draftKey } from '@/stores/unsavedDrafts.store';
import { useProjectContainers } from '@/components/config/AllContainers';
import styles from '@/pages/GradesPage.module.scss';

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result as string);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

/** Vignette d'un cosmetic + actions (télécharger / remplacer / supprimer).
 * Le remplacement de texture est mis en attente (brouillon, barre du bas) ; la suppression est immédiate. */
function Thumb({
  containerId, project, type, name, index, undeclared, onChanged,
}: {
  /** Peut être vide : projet sans serveur déclaré, on travaille alors avec l'index du pack. */
  containerId: string;
  project: string;
  type: 'nametag' | 'ranktag';
  name: string;
  index: number;
  /** Présent dans le pack mais absent du NametagSettings du container. */
  undeclared?: boolean;
  onChanged: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [bust, setBust] = useState(0);
  const [pending, setPending] = useState<string | null>(() => {
    const p = getDraftPayload(draftKey('cosmetic-texture', containerId, `${type}:${name}`));
    return typeof p === 'string' ? p : null;
  });
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!name) { setUrl(null); return; }
    let active = true;
    let created: string | null = null;
    apiService.getCosmeticBlobUrl(containerId, type, name, bust || undefined, index).then((r) => {
      if (!active) { if (r) URL.revokeObjectURL(r); return; }
      created = r;
      setUrl(r);
    });
    return () => { active = false; if (created) URL.revokeObjectURL(created); };
  }, [containerId, type, name, bust, index]);

  const onPick = async (f: File) => {
    if (!f.type.includes('png')) return;
    setPending(await readAsDataUrl(f)); // mis en attente, écrit à l'« Enregistrer tout »
    if (fileRef.current) fileRef.current.value = '';
  };

  const save = async () => {
    if (!pending) return true;
    const res = await apiService.replaceCosmeticTexture(containerId, type, name, pending, index);
    const ok = !!res.data?.success;
    if (ok) { setPending(null); setBust(Date.now()); }
    return ok;
  };

  useSnapshotDraft({
    kind: 'cosmetic-texture',
    groupId: containerId || project,
    identifier: `${type}:${name}`,
    label: `Texture · ${name}`,
    dirty: !!pending,
    payload: pending,
    save,
  });

  const onDelete = async () => {
    const label = type === 'ranktag' ? 'rank tag' : 'ornement';
    if (!window.confirm(`Supprimer le ${label} « ${name} » ? Son PNG, son entrée du render controller et sa ligne dans player.entity.json seront retirés, et il sera retiré des containers du projet (à publier via Déploiement).`)) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await apiService.deleteCosmetic(containerId, project, type, name, index);
      if (res.data?.success) onChanged();
      // Sans retour d'erreur, un echec restait invisible : on affiche le message du backend.
      else setError(res.data?.message ?? res.error ?? 'Suppression impossible.');
    } finally {
      setDeleting(false);
    }
  };

  const preview = pending ?? url;

  return (
    <div className={styles.cosmeticCard}>
      <div className={styles.cosmeticThumb}>
        {preview ? <img src={preview} alt={name} /> : <span className={styles.cosmeticMissing}>—</span>}
        {pending && <span className={styles.cosmeticPending}>modifié</span>}
      </div>
      <span className={styles.cosmeticName}>{name}</span>
      {undeclared && (
        <span className={styles.muted} title="Présent dans le pack mais absent du NametagSettings du serveur">
          non déclaré en config
        </span>
      )}
      <div className={styles.cosmeticActions}>
        {url && (
          <a className={styles.cosmeticAction} href={url} download={`${name}.png`} title="Télécharger">
            <Download size={14} />
          </a>
        )}
        <button className={styles.cosmeticAction} disabled={deleting} onClick={() => fileRef.current?.click()} title="Remplacer la texture (mis en attente)">
          <Upload size={14} />
        </button>
        <button className={`${styles.cosmeticAction} ${styles.cosmeticActionDanger}`} disabled={deleting} onClick={onDelete} title="Supprimer">
          {deleting ? <Loader2 size={14} className={styles.spin} /> : <Trash2 size={14} />}
        </button>
        <input ref={fileRef} type="file" accept="image/png" style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); }} />
      </div>
      {error && <span className={styles.error}>{error}</span>}
    </div>
  );
}

/** Listing des ornements (nametags) et rank tags existants du pack, avec aperçu + actions.
 * `type` filtre l'affichage à un seul type (utilisé sous l'onglet correspondant).
 *
 * La liste vient du **pack** (render controller) et non de la config d'un container : un projet peut
 * n'avoir aucun serveur déclaré dans le panel (cas NationsGlory) et les cosmetics doivent quand même
 * s'afficher. Quand un container existe, sa config sert seulement à marquer ce qui n'y est pas déclaré. */
export function CosmeticList({ refreshKey, type }: { refreshKey?: number; type?: 'nametag' | 'ranktag' }) {
  const project = useGameStore((s) => s.activeGameId);
  const { containers, loaded: containersLoaded } = useProjectContainers();
  const [containerId, setContainerId] = useState('');
  /** Container de référence cherché : sans ça, « aucun serveur » s'afficherait pendant la recherche. */
  const [refResolved, setRefResolved] = useState(false);
  const [packNametags, setPackNametags] = useState<PackCosmeticList | null>(null);
  const [packRanktags, setPackRanktags] = useState<PackCosmeticList | null>(null);
  const [declaredNametags, setDeclaredNametags] = useState<string[]>([]);
  const [declaredRanktags, setDeclaredRanktags] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [localReload, setLocalReload] = useState(0);

  const showNametags = !type || type === 'nametag';
  const showRanktags = !type || type === 'ranktag';

  // Ce qui existe dans le pack (source de vérité de l'affichage).
  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      showNametags ? apiService.listPackCosmetics('nametag') : Promise.resolve({ data: undefined }),
      showRanktags ? apiService.listPackCosmetics('ranktag') : Promise.resolve({ data: undefined }),
    ]).then(([n, r]) => {
      if (!active) return;
      setPackNametags(n.data ?? null);
      setPackRanktags(r.data ?? null);
      setLoading(false);
    });
    return () => { active = false; };
  }, [project, refreshKey, localReload, showNametags, showRanktags]);

  // Ce qui est déclaré en config serveur (facultatif : sert à signaler les manquants). Même container
  // de référence que les réglages des grades : le premier (par id) qui porte la config premium ; à
  // défaut le premier container, où rien n'est déclaré.
  useEffect(() => {
    setRefResolved(false);
    if (!containersLoaded) { setContainerId(''); return; } // changement de projet : rien de l'ancien
    let active = true;
    (async () => {
      for (const c of containers) {
        const r = await apiService.getContainerPremium(c.id);
        if (!active) return;
        if (r.data?.found) {
          setContainerId(c.id);
          setDeclaredNametags(r.data.nametags ?? []);
          setDeclaredRanktags(r.data.ranktags ?? []);
          setRefResolved(true);
          return;
        }
      }
      setContainerId(containers[0]?.id ?? '');
      setDeclaredNametags([]);
      setDeclaredRanktags([]);
      setRefResolved(true);
    })();
    return () => { active = false; };
  }, [containers, containersLoaded, refreshKey, localReload]);

  if (loading) return <div className={styles.loadingState}><div className={styles.spinner} /><span>Chargement…</span></div>;

  const bump = () => setLocalReload((k) => k + 1);
  const noContainer = containersLoaded && refResolved && !containerId;

  const section = (
    kind: 'nametag' | 'ranktag',
    icon: React.ReactNode,
    title: string,
    list: PackCosmeticList | null,
    declared: string[],
  ) => {
    const items = list?.items ?? [];
    return (
      <>
        <div className={styles.sectionLabel}>{icon} {title} ({items.length})</div>
        {items.length === 0 ? (
          <p className={styles.muted}>
            {list && !list.found
              ? `Aucun render controller ${kind === 'ranktag' ? 'rank_tag' : 'cosmetic_tag'} dans le pack du projet (${list.controller}). Synchronise le pack cosmetics, ou vérifie qu'il porte bien ce fichier.`
              : kind === 'ranktag' ? 'Aucun rank tag.' : 'Aucun ornement.'}
          </p>
        ) : (
          <div className={styles.cosmeticGrid}>
            {items.map((it) => (
              <Thumb
                key={`${it.index}:${it.name}`}
                containerId={containerId}
                project={project}
                type={kind}
                name={it.name}
                index={it.index}
                undeclared={refResolved && !!containerId && !declared.includes(it.name)}
                onChanged={bump}
              />
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <>
      {noContainer && (
        <p className={styles.muted}>
          <AlertTriangle size={13} /> Aucun serveur déclaré pour ce projet : les cosmetics ci-dessous existent
          dans le resource pack, mais aucun index n'est écrit en config, donc rien ne s'applique en jeu tant
          qu'un container du projet n'est pas enregistré dans le panel.
        </p>
      )}
      {showNametags && section('nametag', <Sparkles size={14} />, 'Ornements existants', packNametags, declaredNametags)}
      {showRanktags && section('ranktag', <Award size={14} />, 'Rank tags existants', packRanktags, declaredRanktags)}
    </>
  );
}
